import { Loader2, MailIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import type { TGoogleSignInSchema } from "@/api/auth";
import type { GoogleOAuthTokens } from "@/hooks/use-google-auth";
import type { FormSchema } from "@/lib/schema";

import { authenticateUser, googleSign } from "@/api/auth";
import {
	CustomFormField,
	PasswordFields
} from "@/components/form/custom-formfields";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useAuthenticateUser } from "@/hooks/use-authenticate";
import { useGoogleOAuthSignIn } from "@/hooks/use-google-auth";
import { useResendEmail } from "@/hooks/use-verify-email";
import { AuthFormSchema } from "@/lib/schema";
import useAuthStore from "@/store/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/sign-in")({
	loader: () => {
		const { user } = useAuthStore.getState();

		if (user) {
			const userType = user.userType === "BUYER" ? "buyer" : "vendor";
			throw redirect({ to: `/${userType}/home` });
		}
	},
	component: SignInForm
});

function SignInForm() {
	const navigate = useNavigate();
	const resendEmailMutation = useResendEmail();

	const googleSignIn = useGoogleOAuthSignIn();
	const authMutation = useAuthenticateUser(data =>
		authenticateUser(data as FormSchema, "sign-in")
	);
	const googleMutation = useAuthenticateUser(data =>
		googleSign(data as TGoogleSignInSchema)
	);

	const form = useForm<FormSchema>({
		resolver: zodResolver(AuthFormSchema("sign-in")),
		defaultValues: {
			email: "",
			password: ""
		}
	});

	const handleGoogleSignIn = useCallback(async (response: GoogleOAuthTokens) => {
		const data = { token: response.id_token };

		try {
			await googleMutation.mutateAsync({
				data,
				type: "sign-in"
			});
		}
		catch (error) {
			console.error("Error during Google sign-in:", error);
		}
	}, [googleMutation]);

	const handleSubmit = async (values: FormSchema) => {
		try {
			await authMutation.mutateAsync({
				data: { ...values },
				type: "sign-in"
			});
		}
		catch (error) {
			console.error("Authentication error:", error);
		}
	};

	const googleHandledRef = useRef(false);

	useEffect(() => {
		const handleAuthSuccess = () => {
			let redirectTo = "/buyer/home";
			if (typeof window !== "undefined") {
				const stored = window.sessionStorage.getItem("afterLoginRedirect");
				if (stored) {
					redirectTo = stored;
					window.sessionStorage.removeItem("afterLoginRedirect");
				}
				else {
					// Check both authMutation and googleMutation for userType
					const userType = authMutation.data?.data.user.userType
						|| googleMutation.data?.data.user.userType;
					if (userType) {
						redirectTo = `/${userType.toLowerCase()}/home`;
					}
				}
			}
			navigate({ to: redirectTo, replace: true });
		};

		if (authMutation.isSuccess || googleMutation.isSuccess)
			handleAuthSuccess();
		// Only trigger Google backend auth once when token exchange succeeds
		if (googleSignIn.isSuccess && !googleHandledRef.current) {
			googleHandledRef.current = true;
			handleGoogleSignIn(googleSignIn.data);
		}
		// Reset on Google errors so user can retry with a different account
		if (googleSignIn.isError) {
			googleHandledRef.current = false;
		}
		if (googleMutation.isError) {
			googleHandledRef.current = false;
		}
	}, [
		authMutation.isSuccess,
		googleMutation.isSuccess,
		googleMutation.isError,
		googleSignIn.isSuccess,
		googleSignIn.isError,
		googleSignIn.data,
		authMutation.data,
		googleMutation.data,
		navigate,
		handleGoogleSignIn
	]);

	return (
		<>
			<div className="mb-4">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-2 transition-colors duration-150 hover:bg-gray-200"
					onClick={() => window.history.back()}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>
			<section className="mx-auto w-full max-w-md">
				<AuthHeader
					title="Hi, Welcome Back"
					subtitle="Please fill in your details to get access"
					onGoogleSignIn={() => googleSignIn.signIn()}
				/>

			{authMutation.error?.message.includes("Email not verified.")
				? (
						<div className="flex flex-col gap-4">
							<Button
								onClick={async () => {
									const { data } = await resendEmailMutation.mutateAsync({ email: form.getValues("email"), endpoint: "send_verify_email" });
									navigate({
										to: "/verify-email",
										state: {
											verifyEmailOpts: {
												email: form.getValues("email"),
												hash: data
											}
										}
									});
								}}
								className="h-14 w-full"
							>
								Verify Now
							</Button>
							<Button
								asChild
								variant="secondary"
								className="h-14 w-full"
							>
								<Link
									to="/sign-in"
									onClick={() => authMutation.reset()}
								>
									Later
								</Link>
							</Button>
						</div>
					)
				: (

						<Form {...form}>
							<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
								<ErrorMessage error={authMutation.error || googleMutation.error || googleSignIn.error} />
								<CustomFormField
									control={form.control}
									placeholder="Enter your email"
									className="h-14"
									type="email"
									Icon={MailIcon}
									name="email"
								/>

								<PasswordFields
									control={form.control}
									showConfirm={false}
								/>

								<span className="block text-right">
									<Link
										to="/forgot-password"
										className="font-medium text-primary max-lg:text-start"
									>
										Forgot Password?
									</Link>
								</span>
								<Button disabled={authMutation.isPending || googleMutation.isPending} type="submit" className="h-14 w-full">
									<LoadingButton isLoading={authMutation.isPending || googleMutation.isPending} text="Sign In" />
								</Button>
							</form>
						</Form>

					)}
			<div className="my-4 flex justify-center gap-1 max-sm:justify-start">
				<p className="text-14 max-sm:text-9 font-normal text-gray-600">
					Don't have an account?
				</p>
				<Link to="/user-type" className="font-medium text-primary">
					Sign Up
				</Link>
			</div>
			</section>
		</>
	);
}

function LoadingButton({ isLoading,	text }: {
	isLoading: boolean;
	text: string;
}) {
	return (
		<>
			{isLoading
				? (
						<>
							<Loader2 size={20} className="animate-spin" />
							{" "}
							Loading....
						</>
					)
				: (
						text
					)}
		</>
	);
}

function AuthHeader({
	title,
	subtitle,
	onGoogleSignIn
}: {
	title: string;
	subtitle: string;
	onGoogleSignIn: () => void;
}) {
	return (
		<>
			<div className="my-4">
				<h1 className="mb-12 h-1 text-3xl font-semibold leading-9 max-sm:text-2xl">
					{title}
				</h1>
				<p className="text-16 font-light leading-4">{subtitle}</p>
			</div>
			<Button
				onClick={onGoogleSignIn}
				variant="secondary"
				className="group mt-1 h-14 w-full gap-6 hover:bg-primary"
			>
				<img src="/google.svg" width={20} height={20} alt="google" />
				<span className="group-hover:text-white">Sign in with Google</span>
			</Button>
			<div className="my-8 flex items-center">
				<hr className="grow border-t border-gray-300" />
				<span className="mx-2 text-gray-400">Or Sign in with Email</span>
				<hr className="grow border-t border-gray-300" />
			</div>
		</>
	);
}

function ErrorMessage({ error }: { error: Error | null }) {
	if (!error)
		return null;

	return (
		<p className="text-center text-destructive">
			{error.stack?.includes("401")
				? error.message
				: "An error occurred. Please try again."}
		</p>
	);
}
